import { useMutation } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { addDays } from 'date-fns';
import React, { useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { showToastMessage } from 'src/coach/service/coach.utils';
import { Button, InputField } from 'src/shared';
import { useLoading } from 'src/shared/hooks/use-loading';
import { useModal } from 'src/shared/hooks/use-modal';
import * as Yup from 'yup';
import { useAsyncTaskStatus } from '../../class-details/hooks/async-task-status';
import TimeOffAlert from '../components/time-off-alert';
import TimeOffConflicts from '../components/time-off-conflicts';
import { upsertScheduledTimeOffAsyncMutation } from '../graphql/scheduled-time-off-mutation';

const validationSchema = Yup.object().shape({
  categoryId: Yup.string().required('Category is required'),
  startDate: Yup.string().required('Please enter start date.'),
  endDate: Yup.string()
    .required('Please enter end date.')
    .test(
      'is-greater',
      'End date should not be earlier than the start date.',
      function (value) {
        const { startDate } = this.parent;
        if (startDate && value) {
          return new Date(value) >= new Date(startDate);
        }
        return true;
      }
    ),
  allDay: Yup.boolean(),
  startTime: Yup.string().required('Please enter start time.'),
  endTime: Yup.string()
      .required('Please enter end time.')
      .test(
        'is-greater',
        'End time should be greater than start time.',
        function (value) {
          const { startTime } = this.parent;
          if (startTime && value) {
            const start = new Date(`01/01/2020 ${startTime}`);
            const end = new Date(`01/01/2020 ${value}`);
            return end > start;
          }
          return true;
        },
      ),
  status: Yup.string().oneOf(['Pending', 'Approved', 'Rejected'], 'Invalid status'),
  franchiseId: Yup.string().required('Please select a franchise'),
});

const AddEditTimeOffModal = ({coachId, open, handleClose, initialData={},franchises=[],refetch=()=>{} }) => {
  const {isOpen:isOpenConflicts, actions:conflictsActions,context:conflicts} = useModal();
  const {loading:loadingState} = useLoading();

  const [comment, setComment] = React.useState('');
  const isEdit = Boolean(initialData?.id);
  const [categoryOptions, setCategoryOptions] = React.useState([]);
  const {
    handleSubmit,
    register,
    formState: { errors },
    setValue,
    control,
    getValues,
    watch,
  } = useForm({
    defaultValues: {
      franchiseId: initialData?.franchiseId || '',
      categoryId: initialData?.categoryId || '',
      startDate: initialData?.startDate ? new Date(initialData.startDate).toISOString().split('T')[0] : '',
      endDate: initialData?.endDate ? new Date(initialData.endDate).toISOString().split('T')[0] : '',
      allDay: initialData?.allDay || false,
      startTime: initialData?.startTime || null,
      endTime: initialData?.endTime || null,
      status: initialData?.status || 'Pending',
      paid: initialData?.paid || false,
      coachId:coachId
    },
    resolver: yupResolver(validationSchema),
  });

  const {setAsyncTaskExtraData,asyncTaskExtraData,setAsyncTaskId} = useAsyncTaskStatus({
    onComplete:(data)=>{
      loadingState.stop();

      if(data?.result?.conflicts?.length){
      conflictsActions.open(data?.result?.conflicts);
      return;
    }
      refetch();
      showToastMessage(`Scheduled Time off ${initialData?.id ? 'updated' : 'created'} successfully`);
      if(asyncTaskExtraData?.close){
        handleClose();
      }
      loadingState.stop();

    },
    onFail:(error)=>{
      showToastMessage(
					"Could not able to create Scheduled Time Off",
					"error",
				);
      loadingState.stop();

    },
    poolingInterval: 1000,
  })
  const [upsertTimeOff] = useMutation(upsertScheduledTimeOffAsyncMutation);
  const onSubmit = (
			close = true,
      allowStaffingWithConflicts = false,
		)=>async () => {
      loadingState.start();
      conflictsActions.close();
			const values = getValues();
			const input = {
				id: initialData?.id,
				franchiseId: Number(values.franchiseId),
				categoryId: Number(values.categoryId),
				startDate: values.startDate,
				endDate: values.endDate,
				startTime: values.startTime,
				endTime: values.endTime,
				status: "Pending",
				isPaid: values.paid,
				coachId: Number(coachId),
				comment: comment,
				sendNotificationAdmins: isEdit ? false : true,
        allowStaffingWithConflicts,
			};
			const response = await upsertTimeOff({ variables: { input } });

			if (response?.data?.upsertScheduledTimeOffAsync?.id) {
				setAsyncTaskId(response?.data?.upsertScheduledTimeOffAsync?.id);
        setAsyncTaskExtraData({close,conflictsActions});
			} else {
      loadingState.stop();

				showToastMessage(
					"Could not able to create Scheduled Time Off",
					"error",
				);
			}
		}

  const allDay = watch('allDay');

  const franchiseId = watch('franchiseId');

  const minDateToApply = useMemo(() => {
			const selectedFranchise = franchises.find((f) => f.id == franchiseId);
			return addDays(
				new Date(),
				Number(
					selectedFranchise?.settings?.timeOffNotice || 0,
				),
			)?.toISOString()?.split("T")[0];
		}, [franchiseId]);


  useEffect(() => {
    //when a franchise is selected set category options for that franchise
    if (franchiseId) {
      const selectedFranchise = franchises.find(f => f.id == franchiseId);
      setCategoryOptions(selectedFranchise?.scheduledTimeOffCategories||[]);
    }

    //set by default franchise and category if the length of franchise and category is one
    if(!initialData?.id && franchises.length===1){
      setValue('franchiseId',franchises[0].id);
      setCategoryOptions(franchises[0]?.scheduledTimeOffCategories||[]);
      if(franchises[0].scheduledTimeOffCategories.length===1){
        setValue('categoryId',franchises[0].scheduledTimeOffCategories[0].id);
        setValue('paid',franchises[0]?.scheduledTimeOffCategories[0]?.isPaid);
      }
    }
  }
  , [franchiseId, franchises]);

  const showForm = useMemo(() => {
    const selectedFranchise = franchises.find((f) => f.id == franchiseId);
    if(isEdit && initialData?.franchiseId == franchiseId){
      return true;
    }
    return selectedFranchise?.settings?.canAddTimeOff;
  }
  , [franchiseId, franchises]);

  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={handleClose}>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography>{initialData?.id ?'Edit' : 'Add'} Time Off</Typography>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
      
					<Box mb={2}>
 {franchises?.length > 1 && (
					<Controller
						name="franchiseId"
						control={control}
						render={({ field }) => (
							<TextField
								{...field}
								fullWidth
								select
								label="Select Franchise"
								variant="outlined"
								margin="normal"
								onChange={(e) => {
									const franchiseId = e.target.value;
									const selectedFranchise = franchises.find(
										(f) => f.id === franchiseId,
									);
									setValue("franchiseId", franchiseId);
									setValue("categoryId", "");
									setCategoryOptions(
										selectedFranchise.scheduledTimeOffCategories,
									);
								}}
								error={!!errors.franchiseId}
								helperText={
									errors.franchiseId ? errors.franchiseId.message : ""
								}
							>
								{franchises.map((franchise) => (
									<MenuItem key={franchise.id} value={franchise.id}>
										{franchise.name}
									</MenuItem>
								))}
							</TextField>
						)}
					/>
				)}
          {showForm ? 
          <>
						<Controller
							{...register("categoryId")}
							name="categoryId"
							control={control}
							render={({ field }) => (
								<TextField
									fullWidth
									select
									label="Category"
									variant="outlined"
									margin="normal"
									error={!!errors.categoryId}
									helperText={
										errors.categoryId ? errors.categoryId.message : ""
									}
									{...field}
									onChange={(e) => {
										const categoryId = e.target.value;
										setValue("categoryId", categoryId);
										const selectedFranchise = franchises.find(
											(f) => f.id == franchiseId,
										);
										const selectedCategory =
											selectedFranchise.scheduledTimeOffCategories.find(
												(c) => c.id == categoryId,
											);
										setValue("paid", selectedCategory.isPaid);
									}}
								>
									{categoryOptions.map((category) => (
										<MenuItem key={category.id} value={category.id}>
											{category.name}
										</MenuItem>
									))}
								</TextField>
							)}
						/>
						
            <div className='tw-flex tw-flex-col md:tw-flex-row tw-justify-between'>
						<InputField
             onClick={(e) => e.target.showPicker()}
             onKeyDown={(e) => e.preventDefault()} 
							{...register("startDate")}
							className="mb-12 tw-w-full md:tw-w-[calc(50%-20px)]"
							type="date"
							min={minDateToApply}
                  inputClass="tw-w-full"

							$label="Start Date"
							error={errors?.startDate?.message}
						/>
						<InputField
              onClick={(e) => e.target.showPicker()}
               onKeyDown={(e) => e.preventDefault()} 
							{...register("endDate")}
							type="date"
							min={minDateToApply}
                  inputClass="tw-w-full"

							className="mb-12 tw-w-full md:tw-w-[calc(50%-20px)]"

							$label="End Date"
							error={errors?.endDate?.message}
						/>
            </div>
            <FormControlLabel
							control={
								<Checkbox
									{...register("allDay")}
									onChange={(e) => {
										setValue("allDay", e.target.checked);
										setValue("startTime", "00:00");
										setValue("endTime", "23:59");
									}}
									color="primary"
									defaultChecked={initialData?.allDay || false}
								/>
							}
							label="All Day"
						/>
						{!allDay && (
							            <div className='tw-flex tw-flex-col md:tw-flex-row tw-justify-between'>
								<InputField
									{...register("startTime")}
									type="time"
                  inputClass="tw-w-full"
																className="mb-12 tw-w-full md:tw-w-[calc(50%-20px)]"

									$label="Start Time"
									error={errors?.startTime?.message}
								/>
								<InputField
									{...register("endTime")}
									type="time"
                  inputClass="tw-w-full"
																className="mb-12 tw-w-full md:tw-w-[calc(50%-20px)]"

									$label="End Time"
									error={errors?.endTime?.message}
								/>
							</div>
              )}
            <div className="label-text">Reason</div>
            <textarea
              className="tw-w-full tw-p-2 tw-border tw-border-gray-300 tw-rounded-md"
              onChange={(e) => setComment(e.target.value)}
              placeholder="Please enter the reason for requesting this time off"
              type="text"
              value={comment}
            />
						<Box display="flex" alignItems="center" mt={2}>
							<Typography variant="body1" sx={{ marginRight: 2 }}>
								Status
							</Typography>
							<Typography variant="body2" color="textSecondary">
								{initialData?.status || "Pending"}
							</Typography>
						</Box>
            </>
          :
          //only show this alert if a franchise is selected
          franchiseId && <TimeOffAlert franchise={franchises.find(f=>f.id==franchiseId)}/>
          }
					</Box>
      </DialogContent>

      <DialogActions className="tw-flex-col sm:tw-flex-row tw-gap-1 tw-items-center sm:tw-justify-end">
        {showForm && (
					<div className="tw-flex tw-gap-1 tw-w-full sm:tw-justify-end sm:tw-w-auto">
            <Button
              $loading={loadingState.get}
              type='button'
							disabled={loadingState.get}
							onClick={handleSubmit(onSubmit(true))}
							color="primary"
              variant="contained"
              className="tw-w-full sm:tw-w-auto"
						>
							Save & Close
						</Button>
            <Button
               $loading={loadingState.get}
              type='button'
							disabled={loadingState.get}
							onClick={handleSubmit(onSubmit(false))}
							color="primary"
              variant="contained"
              className="tw-w-full sm:tw-w-auto"
						>
							Save & Add New
						</Button>
					</div>
				)}
        <Button
					disabled={loadingState.get}
         className="outline tw-w-full tw-mt-1 sm:tw-mt-0 sm:tw-ml-2 sm:tw-w-auto tw-outline tw-text-red tw-border-red"
          type='button'
          onClick={handleClose}
        >
          Cancel
        </Button>
      </DialogActions>
      {isOpenConflicts && <TimeOffConflicts onConfirm={
									handleSubmit(onSubmit(true, true))} onClose={()=>conflictsActions.close()} timeOff={getValues()} conflicts={conflicts || []}  />}
    </Dialog>
  );
};

export default AddEditTimeOffModal;
